import { FC, useMemo } from 'react'
import { useFormik } from 'formik'
import { Button, FormInput, InfoAlert, ListLoading, LoadingIndicator } from 'app/components'
import { ButtonType, showAlert, showError } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { EsusConfiguracaoDto, EsusConfiguracaoView } from '../../core/_esusModels'
import { esusConfigSchema } from './validation/esusConfigSchema'
import {
  isPermissaoNegadaEsus,
  MSG_SEM_PERMISSAO_ESUS,
  usePermissaoEsusConfig,
} from '../../core/_esusPermissao'
import { updateEsusConfigEmpresa, updateEsusConfigUnidade } from '../../core/_requests'

// Precisa casar com EsusConfiguracaoResponseDto.VERSAO_LEDI_PADRAO no backend — só há
// implementação de EsusLediSerializer para 8.4.2 (br.gov.saude.esusab.v842) hoje.
export const VERSAO_LEDI_PADRAO = '8.4.2'

type Props = {
  empresaId: number
  unidadeId?: number
  config: EsusConfiguracaoView | undefined
  onSaveSuccess: (updated: EsusConfiguracaoView) => void
  onCancel: () => void
}

export const EsusConfigForm: FC<Props> = ({
  empresaId,
  unidadeId,
  config,
  onSaveSuccess,
  onCancel,
}) => {
  const { t } = useT()
  const isUnidadeMode = !!unidadeId
  const efetiva = config?.efetiva
  const podeEditar = usePermissaoEsusConfig()

  // A raiz de `config` traz somente os valores PRÓPRIOS (da unidade ou da empresa).
  // No modo unidade, campo em branco herda da empresa — o valor herdado aparece
  // apenas como placeholder e nunca é reenviado como valor próprio.
  const initialValues = {
    urlPec: config?.urlPec || '',
    usuario: config?.usuario || '',
    senha: '',
    contraChave: '',
    uuidInstalacao: config?.uuidInstalacao || '',
    cpfOuCnpj: config?.cpfOuCnpj || '',
    nomeOuRazaoSocial: config?.nomeOuRazaoSocial || '',
    versaoLedi: config?.versaoLedi || (isUnidadeMode ? '' : VERSAO_LEDI_PADRAO),
  }

  const herdadoPlaceholder = (valorEfetivo: string | undefined, padrao: string) =>
    isUnidadeMode && valorEfetivo ? `Herdado da instituição: ${valorEfetivo}` : padrao

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => esusConfigSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      if (!podeEditar) {
        showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_ESUS, icon: 'warning' })
        return
      }

      setSubmitting(true)
      try {
        // Campos não-secretos vão sempre (inclusive '' para limpar e voltar a herdar);
        // senha/contra-chave em branco significam "manter a atual" e são omitidas.
        const payload: EsusConfiguracaoDto = {
          urlPec: values.urlPec.trim(),
          usuario: values.usuario.trim(),
          senha: values.senha.trim() || undefined,
          contraChave: values.contraChave.trim() || undefined,
          uuidInstalacao: values.uuidInstalacao.trim(),
          cpfOuCnpj: values.cpfOuCnpj.trim(),
          nomeOuRazaoSocial: values.nomeOuRazaoSocial.trim(),
          versaoLedi: values.versaoLedi.trim(),
        }

        let updated: EsusConfiguracaoView
        if (isUnidadeMode && unidadeId) {
          updated = await updateEsusConfigUnidade(empresaId, unidadeId, payload)
        } else {
          updated = await updateEsusConfigEmpresa(empresaId, payload)
        }

        showAlert({
          title: 'Sucesso!',
          text: 'Configuração e-SUS salva com sucesso!',
          icon: 'success',
        })
        onSaveSuccess(updated)
      } catch (err) {
        // Rede de segurança: o backend é a autoridade real sobre `Esus:config`.
        if (isPermissaoNegadaEsus(err)) {
          showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_ESUS, icon: 'warning' })
        } else {
          showError(err)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const camposDesabilitados = formik.isSubmitting || !podeEditar

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <InfoAlert variant='warning' title='A credencial do PEC aparece uma só vez'>
        <p className='mb-2'>
          O PEC e-SUS APS exibe a <strong>senha</strong> e a <strong>contra-chave</strong> apenas no
          momento em que a credencial é gerada. O LauduzOne as armazena cifradas e não as mostra
          novamente, em nenhuma tela.
        </p>
        <p className='mb-0'>
          Se você perder a credencial, não há como recuperá-la:{' '}
          <strong>inative-a no PEC, gere uma nova</strong> e cadastre-a aqui.
        </p>
      </InfoAlert>

      {!podeEditar && (
        <InfoAlert variant='info' title='Somente leitura'>
          {MSG_SEM_PERMISSAO_ESUS}
        </InfoAlert>
      )}

      <div className='row mb-3'>
        <div className='col-md-8'>
          <FormInput
            formik={formik}
            name='urlPec'
            label='URL do PEC e-SUS APS'
            placeholder={herdadoPlaceholder(efetiva?.urlPec, 'Ex: https://pec.municipio.gov.br')}
            isDisabled={camposDesabilitados}
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='versaoLedi'
            label='Versão do LEDI'
            placeholder={herdadoPlaceholder(efetiva?.versaoLedi, `Ex: ${VERSAO_LEDI_PADRAO}`)}
            isDisabled={camposDesabilitados}
          />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='usuario'
            label='Usuário do PEC'
            placeholder={herdadoPlaceholder(efetiva?.usuario, 'Digite o usuário de integração')}
            isDisabled={camposDesabilitados}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='senha'
            type='password'
            label={config?.senhaConfigurada ? 'Senha (Configurada ••••••••)' : 'Senha'}
            placeholder={
              config?.senhaConfigurada
                ? '•••••••• (deixe em branco para manter)'
                : isUnidadeMode && efetiva?.senhaConfigurada
                ? 'Herdada da instituição (deixe em branco para herdar)'
                : 'Digite a senha'
            }
            isDisabled={camposDesabilitados}
          />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='contraChave'
            type='password'
            label={
              config?.contraChaveConfigurada
                ? 'Contra-Chave (Configurada ••••••••)'
                : 'Contra-Chave'
            }
            placeholder={
              config?.contraChaveConfigurada
                ? '•••••••• (deixe em branco para manter)'
                : isUnidadeMode && efetiva?.contraChaveConfigurada
                ? 'Herdada da instituição (deixe em branco para herdar)'
                : 'Digite a contra-chave do PEC'
            }
            isDisabled={camposDesabilitados}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='uuidInstalacao'
            label='UUID da Instalação do PEC'
            placeholder={herdadoPlaceholder(efetiva?.uuidInstalacao, 'Digite o UUID da instalação')}
            isDisabled={camposDesabilitados}
          />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='cpfOuCnpj'
            label='CPF ou CNPJ do Transmissor/Gestor'
            placeholder={herdadoPlaceholder(efetiva?.cpfOuCnpj, 'Digite o CPF ou CNPJ')}
            isDisabled={camposDesabilitados}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='nomeOuRazaoSocial'
            label='Nome ou Razão Social do Transmissor'
            placeholder={herdadoPlaceholder(
              efetiva?.nomeOuRazaoSocial,
              'Digite o nome ou razão social'
            )}
            isDisabled={camposDesabilitados}
          />
        </div>
      </div>

      <div className='d-flex justify-content-end gap-2 mt-4'>
        <Button
          type={ButtonType.Reset}
          onClick={onCancel}
          className='btn-secondary'
          size='btn-sm'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        {podeEditar && (
          <Button
            type={ButtonType.Submit}
            className='btn-primary'
            size='btn-sm'
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar Configurações'}
          </Button>
        )}
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
